import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { searchTeams, SearchResultItem } from '../../lib/api';
import {
  MagnifyingGlassIcon,
  TrophyIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';

interface SearchDropdownProps {
  onNavigate?: () => void;       // called after navigation (e.g. close mobile menu)
  className?: string;            // wrapper className override
  inputClassName?: string;       // input className override
}

export default function SearchDropdown({ onNavigate, className, inputClassName }: SearchDropdownProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResultItem[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(-1);
  const navigate = useNavigate();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  // Debounced search
  const doSearch = useCallback((q: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (q.trim().length < 2) {
      setResults([]);
      setIsOpen(false);
      return;
    }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const items = await searchTeams(q);
        setResults(items);
        setIsOpen(items.length > 0);
        setHighlightIdx(-1);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 200);
  }, []);

  useEffect(() => {
    doSearch(query);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query, doSearch]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const goTo = (item: SearchResultItem) => {
    const path = item.type === 'league' ? `/league/${item.slug}` : `/team/${item.slug}`;
    navigate(path);
    setQuery('');
    setIsOpen(false);
    onNavigate?.();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || results.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIdx((i) => (i + 1) % results.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIdx((i) => (i - 1 + results.length) % results.length);
    } else if (e.key === 'Enter' && highlightIdx >= 0) {
      e.preventDefault();
      goTo(results[highlightIdx]);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  return (
    <div ref={wrapperRef} className={`relative ${className || ''}`}>
      {/* Input */}
      <div className="relative">
        <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => { if (results.length > 0) setIsOpen(true); }}
          onKeyDown={handleKeyDown}
          placeholder="Search teams & leagues..."
          className={inputClassName || `w-full pl-10 pr-4 py-2 rounded-lg bg-slate-900/60 border border-slate-600/50
                     text-sm text-slate-200 placeholder-slate-500
                     focus:outline-hidden focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-500/40
                     transition-colors`}
        />
        {loading && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <div className="w-4 h-4 border-2 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin" />
          </div>
        )}
      </div>

      {/* Dropdown results */}
      {isOpen && results.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1.5 bg-slate-800 border border-slate-700/60 rounded-xl shadow-xl shadow-black/30 overflow-hidden z-50 max-h-80 overflow-y-auto">
          {results.map((item, idx) => (
            <button
              key={`${item.type}-${item.id}`}
              onClick={() => goTo(item)}
              onMouseEnter={() => setHighlightIdx(idx)}
              className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                idx === highlightIdx ? 'bg-slate-700/50' : 'hover:bg-slate-700/30'
              }`}
            >
              {/* Icon / Logo */}
              {item.logo ? (
                <img
                  src={item.logo}
                  alt={item.name}
                  className="w-8 h-8 rounded-lg object-contain bg-slate-700/40 p-1 shrink-0"
                />
              ) : (
                <div className="w-8 h-8 rounded-lg bg-slate-700 flex items-center justify-center shrink-0">
                  {item.type === 'league' ? (
                    <TrophyIcon className="w-4 h-4 text-slate-400" />
                  ) : (
                    <UserGroupIcon className="w-4 h-4 text-slate-400" />
                  )}
                </div>
              )}

              {/* Name + meta */}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-slate-200 truncate">
                  {item.name}
                </p>
                <p className="text-xs text-slate-500 truncate">
                  {item.type === 'league' ? 'League' : item.league_name || 'Team'}
                </p>
              </div>

              {/* Type badge */}
              <span className={`shrink-0 text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full ${
                item.type === 'league'
                  ? 'bg-amber-500/15 text-amber-400'
                  : 'bg-sky-500/15 text-sky-400'
              }`}>
                {item.type}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* "No results" state */}
      {isOpen && results.length === 0 && query.trim().length >= 2 && !loading && (
        <div className="absolute top-full left-0 right-0 mt-1.5 bg-slate-800 border border-slate-700/60 rounded-xl shadow-xl shadow-black/30 z-50 px-4 py-6 text-center">
          <p className="text-sm text-slate-500">No results for "{query}"</p>
        </div>
      )}
    </div>
  );
}
